import { formatMapName } from '../../liveSession/lib/formatters';
import { isKnown, mapKey } from '../../strategies/lib/playbook';
import type { LearnStrategy, QueueSource, RoleQueueItem } from '../types';

export const ASSIGN_MARKS_NOTE =
  'Learning marks on this page are yours. The practice queue is the list you were assigning.';

export type LearnMode = 'strategies' | 'lineups';
export type AsMode = 'play' | 'assign';
export type SideFilter = 'all' | 'T' | 'CT';
export type KnowledgeFilter = 'all' | 'known' | 'pending';

export type LearnSearch = {
  mode: LearnMode;
  map: string;
  q: string;
  type: string;
  side: SideFilter;
  grenade: string;
  knowledge: KnowledgeFilter;
  asMode: AsMode;
  playerId: string;
  lineupId: string;
};

export const EMPTY_LEARN_SEARCH: LearnSearch = {
  mode: 'strategies',
  map: 'all',
  q: '',
  type: 'all',
  side: 'all',
  grenade: 'all',
  knowledge: 'all',
  asMode: 'play',
  playerId: '',
  lineupId: '',
};

const SIDES = new Set<SideFilter>(['all', 'T', 'CT']);
const KNOWLEDGE = new Set<KnowledgeFilter>(['all', 'known', 'pending']);

export function parseLearnSearch(params: URLSearchParams, canAssign: boolean): LearnSearch {
  const side = params.get('side')?.toUpperCase() ?? 'all';
  const knowledge = params.get('knowledge') ?? 'all';
  const assigning = canAssign && params.get('as') === 'assign';
  return {
    mode: params.get('mode') === 'lineups' ? 'lineups' : 'strategies',
    map: mapKey(params.get('map')) || 'all',
    q: params.get('q') ?? '',
    type: params.get('type') || 'all',
    side: SIDES.has(side as SideFilter) ? (side as SideFilter) : 'all',
    grenade: params.get('grenade') || 'all',
    knowledge: KNOWLEDGE.has(knowledge as KnowledgeFilter) ? (knowledge as KnowledgeFilter) : 'all',
    asMode: assigning ? 'assign' : 'play',
    playerId: assigning ? (params.get('player') ?? '') : '',
    lineupId: params.get('lineup') ?? '',
  };
}

export function learnSearchString(state: LearnSearch): string {
  const params = new URLSearchParams();
  if (state.mode !== 'strategies') params.set('mode', state.mode);
  if (state.map !== 'all') params.set('map', state.map);
  if (state.q.trim()) params.set('q', state.q);
  if (state.type !== 'all') params.set('type', state.type);
  if (state.side !== 'all') params.set('side', state.side);
  if (state.grenade !== 'all') params.set('grenade', state.grenade);
  if (state.knowledge !== 'all') params.set('knowledge', state.knowledge);
  if (state.asMode === 'assign') {
    params.set('as', 'assign');
    if (state.playerId) params.set('player', state.playerId);
  }
  if (state.mode === 'lineups' && state.lineupId) params.set('lineup', state.lineupId);
  return params.toString();
}

export function catalogFiltersActive(state: LearnSearch): boolean {
  return Boolean(state.q.trim())
    || state.knowledge !== 'all'
    || (state.mode === 'strategies' && (state.type !== 'all' || state.side !== 'all'))
    || (state.mode === 'lineups' && state.grenade !== 'all');
}

export function inMap(map: string | null | undefined, scope: string): boolean {
  return scope === 'all' || mapKey(map) === scope;
}

export function utilityLabel(utilityType: string | null | undefined): string {
  if (utilityType === 'he') return 'HE';
  if (!utilityType) return '';
  return utilityType.charAt(0).toUpperCase() + utilityType.slice(1);
}

export function defaultAssignPlayerId(
  players: { id: string; userId: string | null }[],
  userId: string,
): string {
  return players.find((player) => player.userId !== userId)?.id ?? players[0]?.id ?? '';
}

export function viewingOwnMarks(
  asMode: AsMode,
  selectedUserId: string | null | undefined,
  currentUserId: string | undefined,
): boolean {
  return asMode !== 'assign' || Boolean(currentUserId) && selectedUserId === currentUserId;
}

export function initialOpenStrategyIds(queue: { strategyId: string }[]): string[] {
  return [...new Set(queue.map((item) => item.strategyId))];
}

export function mapChoices(maps: (string | null | undefined)[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const map of maps) {
    const key = mapKey(map);
    if (key && !seen.has(key)) seen.set(key, formatMapName(map) || map || key);
  }
  return [...seen.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([value, label]) => ({ value, label }));
}

export type ScopedStats = {
  known: number;
  total: number;
  open: number;
  mapCount: number;
  coverage: string;
  percent: number;
};

export function percent(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((100 * part) / total);
}

export function strategyStats(
  strategies: LearnStrategy[],
  queue: { map: string; learned: boolean }[],
  scope: string,
): ScopedStats {
  const scoped = strategies.filter((strategy) => inMap(strategy.map, scope));
  const known = scoped.filter((strategy) => isKnown(strategy)).length;
  const open = queue.filter((item) => inMap(item.map, scope) && !item.learned).length;
  const mapCount = new Set(strategies.map((strategy) => mapKey(strategy.map)).filter(Boolean)).size;
  return {
    known,
    total: scoped.length,
    open,
    mapCount,
    percent: percent(known, scoped.length),
    coverage: coverageText(known, scoped.length, 'strategies', scope, mapCount),
  };
}

export function lineupStats(
  lineups: { map: string; id: string }[],
  learnedIds: Set<string>,
  queue: { map: string; utilityUseId: string }[],
  scope: string,
): ScopedStats {
  const scoped = lineups.filter((lineup) => inMap(lineup.map, scope));
  const known = scoped.filter((lineup) => learnedIds.has(lineup.id)).length;
  const open = queue.filter((item) => inMap(item.map, scope) && !learnedIds.has(item.utilityUseId)).length;
  const mapCount = new Set(lineups.map((lineup) => mapKey(lineup.map)).filter(Boolean)).size;
  return {
    known,
    total: scoped.length,
    open,
    mapCount,
    percent: percent(known, scoped.length),
    coverage: coverageText(known, scoped.length, 'lineups', scope, mapCount),
  };
}

function coverageText(known: number, total: number, noun: string, scope: string, mapCount: number): string {
  const label = scope === 'all' ? 'all maps' : formatMapName(scope) || scope;
  if (!total) return scope === 'all' ? `No ${noun} in the book` : `No ${noun} on ${label}`;
  if (scope === 'all') return `${known} of ${total} ${noun} across ${mapCount} maps`;
  return `${known} of ${total} ${noun} on ${label}`;
}

export function outsideOpenCount(
  items: { map: string; learned: boolean }[],
  scope: string,
): number {
  if (scope === 'all') return 0;
  return items.filter((item) => !inMap(item.map, scope) && !item.learned).length;
}

export function outsideNote(count: number, noun: 'role' | 'lineup'): string {
  if (!count) return '';
  return `${count} more ${noun}${count === 1 ? ' is' : 's are'} queued on other maps.`;
}

export function sourceSplit(
  items: { map: string; learned: boolean; source: QueueSource }[],
  scope: string,
): { assigned: number; self: number } {
  const open = items.filter((item) => inMap(item.map, scope) && !item.learned);
  return {
    assigned: open.filter((item) => item.source === 'assigned').length,
    self: open.filter((item) => item.source === 'self').length,
  };
}

export function sourceSplitText(
  split: { assigned: number; self: number },
  options: { assigning: boolean; ownList: boolean; playerName: string },
): string {
  if (!options.assigning || options.ownList) {
    return `${split.assigned} assigned by your strategist · ${split.self} you added`;
  }
  return `${split.assigned} assigned · ${split.self} added by ${options.playerName}`;
}

export function filterStrategies(
  strategies: LearnStrategy[],
  state: LearnSearch,
): LearnStrategy[] {
  const query = state.q.trim().toLowerCase();
  return strategies.filter((strategy) => {
    if (!inMap(strategy.map, state.map)) return false;
    if (state.type !== 'all' && strategy.strategyType !== state.type) return false;
    if (state.side !== 'all' && strategy.side.toUpperCase() !== state.side) return false;
    const known = isKnown(strategy);
    if (state.knowledge === 'known' && !known) return false;
    if (state.knowledge === 'pending' && known) return false;
    if (!query) return true;
    const roles = strategy.roles.map((role) => role.name).join(' ');
    const blob = `${strategy.name} ${strategy.id} ${strategy.description ?? ''} ${strategy.route ?? ''} ${strategy.map} ${roles}`.toLowerCase();
    return blob.includes(query);
  });
}

export function filterLineups<T extends { id: string; name: string; map: string; description: string | null; utilityType: string }>(
  lineups: T[],
  learnedIds: Set<string>,
  state: LearnSearch,
): T[] {
  const query = state.q.trim().toLowerCase();
  return lineups.filter((lineup) => {
    if (!inMap(lineup.map, state.map)) return false;
    if (state.grenade !== 'all' && lineup.utilityType !== state.grenade) return false;
    const known = learnedIds.has(lineup.id);
    if (state.knowledge === 'known' && !known) return false;
    if (state.knowledge === 'pending' && known) return false;
    if (!query) return true;
    return `${lineup.name} ${lineup.id} ${lineup.description ?? ''} ${lineup.map}`.toLowerCase().includes(query);
  });
}

export function moveIds(allIds: string[], visibleIds: string[], id: string, direction: -1 | 1): string[] | null {
  const index = visibleIds.indexOf(id);
  const neighbor = visibleIds[index + direction];
  if (index < 0 || !neighbor) return null;
  const next = allIds.slice();
  const from = next.indexOf(id);
  const to = next.indexOf(neighbor);
  if (from < 0 || to < 0) return null;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function queuedRoleIds(queue: RoleQueueItem[], strategyId: string): Set<string> {
  return new Set(queue.filter((item) => item.strategyId === strategyId).map((item) => item.strategyRoleId));
}

export function listOwner(assigning: boolean, playerName: string): string {
  return assigning ? `${playerName}'s list` : 'your list';
}

export function removeRoleMessage(
  roleName: string,
  strategyName: string,
  source: QueueSource,
  assigning: boolean,
  playerName: string,
): string {
  if (!assigning && source === 'assigned') {
    return `Removed ${roleName} from ${strategyName}. Your strategist assigned it.`;
  }
  return `Removed ${roleName} from ${listOwner(assigning, playerName)}.`;
}

export function removeRolesMessage(
  strategyName: string,
  removed: { source: QueueSource }[],
  assigning: boolean,
): string {
  const assigned = removed.filter((item) => item.source === 'assigned').length;
  if (!assigning && assigned) {
    return `Removed ${removed.length} roles from ${strategyName}, including ${assigned} assigned by your strategist.`;
  }
  return `Removed ${removed.length} roles from ${strategyName}.`;
}

export function removeLineupMessage(name: string, source: QueueSource, assigning: boolean): string {
  if (!assigning && source === 'assigned') return `Removed ${name}. Your strategist assigned it.`;
  return `Removed ${name} from the list.`;
}

export function addedRolesMessage(count: number, strategyName: string): string {
  return `Added ${count} role${count === 1 ? '' : 's'} for ${strategyName}.`;
}

export function addedItemMessage(name: string): string {
  return `Added ${name} to the list.`;
}

export function clearDoneMessage(count: number, noun: 'role' | 'throw'): string {
  return `Cleared ${count} finished ${noun}${count === 1 ? '' : 's'} from the list.`;
}

export function emptyQueueCopy(mode: LearnMode, assigning: boolean, playerName: string): string {
  if (mode === 'lineups') {
    return assigning
      ? `Nothing queued for ${playerName}. Add a throw from the library.`
      : 'Nothing queued. Add a throw from the library, or your strategist can assign one.';
  }
  return assigning
    ? `Nothing queued for ${playerName}. Add a role from the playbook.`
    : 'Nothing queued. Add a role from the playbook, or your strategist can assign one.';
}

export function detailHref(strategyId: string, returnQuery: string, roleId?: string): string {
  const params = new URLSearchParams();
  if (roleId) params.set('role', roleId);
  params.set('from', 'learn');
  if (returnQuery) params.set('return', returnQuery);
  return `/strategies/${encodeURIComponent(strategyId)}?${params.toString()}`;
}
